import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import './Sidebar.css';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Typography, Avatar, useMediaQuery, Menu, MenuItem as MuiMenuItem, Divider, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Assignment, People, AutoStories, MenuBook, Tune, School, LibraryBooks, BarChart, Psychology, AssessmentOutlined, LogoutOutlined, History, QueryStats, PersonOutline, SwapHoriz, ChevronLeft, ChevronRight, Business } from '@mui/icons-material';
import { tokens } from '../../theme';

const BRAND = tokens.color.brand;
const RELOAD_AFTER_SWITCH = false; // set true only if context stops refreshing the user on switch

//const Sidebar = ({ open = false, onClose = () => {} }) => {
const Sidebar = ({ open = false, onClose = () => {}, sx }) => {    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));         // < 600: overlay, hidden by default
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600–899: permanent, forced icons
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));          // >= 900: permanent, user-controlled
    const { user, logout, switchRole } = useUser();
    const navigate = useNavigate();
    const location = useLocation();

    const [personAnchorEl, setPersonAnchorEl] = useState(null);
    // The workspace's name comes with the sign-in (the session user's workspace_name, N47 pt 6.1), for everyone, read
    // as the user changes.
    const workspaceName = user?.workspace_name || user?.workspace?.name || user?.company_name || '';

    const roles = user?.roles || [];
    const hasMultipleRoles = roles.length > 1;
    const currentRoleId = user?.current_role_id ?? user?.role_id;
    const currentRole = roles.find(r => r.id === currentRoleId) || roles[0];

    const effectiveRoleName = (
        currentRole?.description || currentRole?.role_name || user?.role_name || ''
    ).toLowerCase();
    const isLearner = currentRoleId === 2 || effectiveRoleName === 'learner';
    const isAdmin   = effectiveRoleName === 'admin';
    const isManager = effectiveRoleName === 'manager';

    // Removed 2 Nov 2026 (N47 pt 6.1): asking Settings for the workspace name when the session user had none, which only
    // admins may read, so every learner page made a refused (403) request.

    const [collapsed, setCollapsed] = useState(() => {
        try {
            return sessionStorage.getItem('sidebarCollapsed') === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        try {
            sessionStorage.setItem('sidebarCollapsed', String(collapsed));
        } catch {
            // sessionStorage unavailable (private mode, etc.) — degrade to in-memory only
        }
    }, [collapsed]);

    // Tablet is always icon-only; mobile's collapse state is irrelevant
    // since it's a full-width overlay when open; only desktop honors toggle.
    const effectiveCollapsed = isTablet ? true : (isDesktop ? collapsed : false);
    const toggleCollapsed = () => setCollapsed(c => !c);

    // Publish the width so Sidebar.css and Layout.css read one source of
    // truth instead of duplicating pixel values that can drift.
    useEffect(() => {
        const width = effectiveCollapsed ? tokens.sidebar.widthClosed : tokens.sidebar.widthOpen;
        document.documentElement.style.setProperty('--sidebar-width', width);
    }, [effectiveCollapsed]);

    const displayWorkspaceName = workspaceName || 'Beetsol';
    const workspaceInitial = displayWorkspaceName.charAt(0).toUpperCase();
    const personInitial = (user?.first_name || user?.name || 'U').trim().charAt(0).toUpperCase();

    const hasPermission = (permission) => user?.permissions?.includes(permission);

    const handleRoleSwitch = async (roleId) => {
        setPersonAnchorEl(null);
        if (roleId === currentRoleId) return;
        try {
            await switchRole(roleId);
            if (RELOAD_AFTER_SWITCH) {
                window.location.href = '/dashboard';
            } else {
                // Instant: context is updated by switchRole; just route.
                navigate('/dashboard');
                if (isMobile) onClose();
            }
        } catch (e) {
            console.error('Role switch failed:', e);
        }
    };

    // ── Menu model ────────────────────────────────────────────────────────────
    //const dashboardMenuItems = [{ text: 'Home', icon: <Home />, path: '/dashboard' }];
    const dashboardMenuItems = [];
    // The admin site's menu links to its Workspaces list, for a super admin (K11 pt 5.1). Removed 23 Oct 2026 (K11 pt
    // 5.1): no link, so the list opened only by typing /workspace.
    const onAdminSite = window.location.hostname.startsWith('admin.');
    if (onAdminSite && (user?.super_admin === true || user?.super_admin === 1)) {
        dashboardMenuItems.push({ text: 'Workspaces', icon: <Business />, path: '/workspace', match: ['/workspace'] });
    }
    if (isManager) {
        dashboardMenuItems.push({ text: 'Learning Summary', icon: <BarChart />, path: '/dashboard/learning-summary' });
    }

    const learningMenuItems = [
        //{ text: 'Courses', icon: <MenuBook />, path: '/courses', permission: 'view_course' },
        //{ text: 'Learning Paths', icon: <AutoStories />, path: '/learning_path', permission: 'view_learning_path' },
        { text: 'Courses', icon: <MenuBook />, path: '/courses', permission: 'view_course', match: ['/courses', '/course', '/settings/feedback-settings'] },
        { text: 'Learning Paths', icon: <AutoStories />, path: '/learning_path', permission: 'view_learning_path', match: ['/learning_path'] },
    ].filter(item => hasPermission(item.permission));

    const contentLibraryMenuItems = [
        { text: 'Content Library', icon: <Assignment />, path: '/contents', permission: 'view_content' }
    ].filter(item => hasPermission(item.permission));

    const manageMenuItems = [
        //{ text: 'Users', icon: <People />, path: '/users', permission: 'view_users' },
        { text: 'Users', icon: <People />, path: '/users', permission: 'view_users', match: ['/users', '/groups', '/job-roles'] },
    ].filter(item => !item.permission || hasPermission(item.permission));

    // Insights → single entry to the dashboard (sub-pages navigated from there).
    const insightsMenuItems = [
        { text: 'Insights', icon: <QueryStats />, path: '/insights' },
    ].filter(() => isAdmin);

    const configureMenuItems = [
        { text: 'Skills', icon: <Psychology />, path: '/skills' },
        { text: 'Learner Report', icon: <AssessmentOutlined />, path: '/reports/learner-progress' },
        { text: 'Course Report', icon: <AssessmentOutlined />, path: '/reports/course-engagement' },
        //{ text: 'Settings', icon: <Tune />, path: '/settings/feedback' },
        { text: 'Settings', icon: <Tune />, path: '/settings/feedback', match: ['/settings/generalSettings', '/settings/feedback', '/settings/emailSettings', '/settings/embedded'] },
    ].filter(() => isAdmin);

    const learnerMenuItems = [
        { text: 'Catalog', icon: <LibraryBooks />, path: '/learner/catalog' },
        { text: 'My Learnings', icon: <School />, path: '/learner/my-learnings' },
        { text: 'History', icon: <History />, path: '/learner/recent-contents' }
    ].filter(() => isLearner);

    const handleNav = (path) => {
        navigate(path);
        if (isMobile) onClose();
    };

    const MenuItemRow = ({ item, isActive, collapsed }) => {
        const row = (
            <ListItem
                button
                onClick={() => handleNav(item.path)}
                className={`menu-item ${isActive ? 'active' : ''} sidebar-list-item`}
            >
                <Box className="menu-item-content">
                    <ListItemIcon className={`menu-icon ${isActive ? 'active' : ''}`}>
                        {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                        <ListItemText primary={item.text} className={`menu-text ${isActive ? 'active' : ''}`} />
                    )}
                </Box>
            </ListItem>
        );
        return collapsed
            ? <Tooltip title={item.text} placement="right"><span>{row}</span></Tooltip>
            : row;
    };

    const isNavActive = (item, pathname) => {
        const matchers = item.match || [item.path];
        return matchers.some(m => pathname === m || pathname.startsWith(m + '/'));
    };

    const MenuSection = ({ items }) => (
        items.length > 0 ? (
            <div className="menu-section">
                <List>
                    {items.map((item) => (
                        <MenuItemRow key={item.text} item={item} isActive={isNavActive(item, location.pathname)} collapsed={effectiveCollapsed} />
                    ))}
                </List>
            </div>
        ) : null
    );

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? open : true}
            onClose={onClose}
            className={`sidebar ${effectiveCollapsed ? 'sidebar-collapsed' : ''}`}
            ModalProps={{ keepMounted: true }}
            sx={{
                ...(sx || {}),
                ...(isMobile ? {
                    zIndex: (t) => t.zIndex.drawer + 2,
                    '& .MuiDrawer-paper': { zIndex: (t) => t.zIndex.drawer + 2 },
                } : {}),
            }}
        >
            <Box
                className="menu-container"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                {/* ── Workspace mark + name (top) ─────────────────────────────── */}
                <Box
                    onClick={() => { if (!effectiveCollapsed) handleNav('/dashboard'); }}
                    title={displayWorkspaceName}
                    sx={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                        gap: effectiveCollapsed ? 0 : '10px',
                        padding: effectiveCollapsed ? '14px 8px' : '14px 16px 14px',
                        cursor: effectiveCollapsed ? 'default' : 'pointer',
                        borderBottom: '0.5px solid rgba(255,255,255,0.08)',
                        flexShrink: 0,
                    }}
                >
                    {!effectiveCollapsed && (
                        <Box
                            sx={{
                                width: '28px', height: '28px', borderRadius: '7px',
                                background: BRAND, color: tokens.color.sidebarActive, flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '15px', fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
                            }}
                        >
                            {workspaceInitial}
                        </Box>
                    )}
                    {!effectiveCollapsed && (
                        <Typography
                            noWrap
                            sx={{
                                fontSize: '14px', fontWeight: 500, color: tokens.color.sidebarActive,
                                letterSpacing: '-0.01em',
                                flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                        >
                            {displayWorkspaceName}
                        </Typography>
                    )}
                    {isDesktop && (
                        <IconButton
                            onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
                            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            size="small"
                            sx={{ color: tokens.color.sidebarText, flexShrink: 0, p: '4px' }}
                        >
                            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
                        </IconButton>
                    )}
                </Box>

                {/* ── Nav ─────────────────────────────────────────────────────── */}
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: '6px' }}>
                    <MenuSection items={dashboardMenuItems} />
                    {hasPermission('view_course') && <MenuSection items={learningMenuItems} />}
                    {hasPermission('view_content') && <MenuSection items={contentLibraryMenuItems} />}
                    <MenuSection items={manageMenuItems} />
                    {isLearner && <MenuSection items={learnerMenuItems} />}
                    {isAdmin && (
                        <>
                            <MenuSection items={insightsMenuItems} />
                            <MenuSection items={configureMenuItems} />
                        </>
                    )}
                </Box>

                {/* ── Person row (bottom) — opens Profile / roles / Logout ────── */}
                <Box
                    onClick={(e) => setPersonAnchorEl(e.currentTarget)}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '12px 16px', cursor: 'pointer',
                        borderTop: '0.5px solid rgba(255,255,255,0.08)',
                        flexShrink: 0,
                        '&:hover': { background: 'rgba(255,255,255,0.04)' },
                    }}
                >
                    <Avatar
                        sx={{
                            width: 30, height: 30, flexShrink: 0,
                            background: BRAND, color: tokens.color.sidebarActive,
                            fontSize: '13px', fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
                        }}
                    >
                        {personInitial}
                    </Avatar>
                    {!effectiveCollapsed && (
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography noWrap title={user?.name || ''} sx={{ fontSize: '13px', fontWeight: 500, color: tokens.color.sidebarActive, lineHeight: 1.3 }}>
                                {user?.name || 'User'}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', color: tokens.color.sidebarText, lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: '3px' }}>
                                {currentRole?.description || currentRole?.role_name || ''}
                                <span style={{ fontSize: '9px' }}>▾</span>
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Menu
                    anchorEl={personAnchorEl}
                    open={Boolean(personAnchorEl)}
                    onClose={() => setPersonAnchorEl(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: 188, background: tokens.color.sidebarBg,
                                border: `0.5px solid ${tokens.color.borderPrimary}`, borderRadius: '8px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4)', mt: '-6px',
                                '& .MuiMenuItem-root': {
                                    fontSize: '13px', color: tokens.color.textSecondary,
                                    '&:hover': { background: 'rgba(255,255,255,0.08)', color: tokens.color.sidebarActive },
                                },
                            },
                        },
                    }}
                >
                    <MuiMenuItem onClick={() => { setPersonAnchorEl(null); handleNav('/users/profile'); }}>
                        <ListItemIcon sx={{ minWidth: 30 }}><PersonOutline sx={{ fontSize: 18, color: tokens.color.sidebarText }} /></ListItemIcon>
                        Profile
                    </MuiMenuItem>

                    {hasMultipleRoles && <Divider sx={{ borderColor: tokens.color.borderPrimary, my: '4px' }} />}
                    {hasMultipleRoles && (
                        <Typography sx={{ px: '16px', py: '4px', fontSize: '11px', color: tokens.color.textSecondary }}>
                            Switch role
                        </Typography>
                    )}
                    {hasMultipleRoles && roles.map(role => {
                        const active = role.id === currentRoleId;
                        return (
                            <MuiMenuItem
                                key={role.id}
                                onClick={() => handleRoleSwitch(role.id)}
                                sx={{ color: active ? tokens.color.sidebarActive : undefined, fontWeight: active ? 600 : 400 }}
                            >
                                <ListItemIcon sx={{ minWidth: 30 }}><SwapHoriz sx={{ fontSize: 18, color: active ? tokens.color.brand : tokens.color.sidebarText }} /></ListItemIcon>
                                {role.description || role.role_name}
                            </MuiMenuItem>
                        );
                    })}

                    <Divider sx={{ borderColor: tokens.color.borderPrimary, my: '4px' }} />
                    <MuiMenuItem onClick={() => { setPersonAnchorEl(null); logout(); }}>
                        <ListItemIcon sx={{ minWidth: 30 }}><LogoutOutlined sx={{ fontSize: 18, color: tokens.color.sidebarText }} /></ListItemIcon>
                        Logout
                    </MuiMenuItem>
                </Menu>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
